import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { X, Check, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';

const ProfilePictureCropModal = ({ imageUrl, onSave, onClose }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.5);  // Start at 50% so users can see the whole image
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = () => {
    if (croppedAreaPixels) {
      onSave(croppedAreaPixels, rotation);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">Profilbild anpassen</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1">
          {/* Crop Area */}
          <div className="relative h-[400px] bg-gray-900">
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={4/3}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              cropShape="rect"
              showGrid={true}
              restrictPosition={false}
              style={{
                containerStyle: {
                  backgroundColor: '#111827',
                  cursor: 'move'
                },
                mediaStyle: {
                  cursor: 'grab'
                },
                cropAreaStyle: {
                  border: '2px solid rgba(147, 51, 234, 0.8)',
                  boxShadow: '0 0 20px rgba(147, 51, 234, 0.5)'
                }
              }}
            />
            {/* Instruction Overlay */}
            <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-lg text-sm">
              💡 <strong>Tipp:</strong> Klicken & Ziehen um Position zu ändern (horizontal & vertikal)
            </div>
          </div>

          {/* Controls */}
          <div className="p-6 space-y-4 border-t border-gray-200">
          {/* Zoom Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <ZoomIn className="w-4 h-4" />
                Größe anpassen
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={() => setZoom(0.5)}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-medium"
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setZoom(Math.max(0.3, zoom - 0.1))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Verkleinern"
              >
                <ZoomOut className="w-5 h-5 text-gray-700" />
              </button>
              <input
                type="range"
                min={0.3}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #2B2142 0%, #2B2142 ${((zoom - 0.3) / 2.7) * 100}%, #e5e7eb ${((zoom - 0.3) / 2.7) * 100}%, #e5e7eb 100%)`
                }}
              />
              <button
                onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Vergrößern"
              >
                <ZoomIn className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            <p className="text-xs text-gray-500">Bereich: 30% (verkleinern) bis 300% (vergrößern) • Mit Maus: Bild ziehen zum Verschieben</p>
          </div>

          {/* Rotation Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <RotateCw className="w-4 h-4" />
                Drehung
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{rotation}°</span>
                <button
                  onClick={() => setRotation(0)}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-medium"
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setRotation((rotation - 90 + 360) % 360)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="90° nach links"
              >
                <RotateCw className="w-5 h-5 text-gray-700 transform -scale-x-100" />
              </button>
              <input
                type="range"
                min={0}
                max={360}
                step={1}
                value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #2B2142 0%, #2B2142 ${(rotation / 360) * 100}%, #e5e7eb ${(rotation / 360) * 100}%, #e5e7eb 100%)`
                }}
              />
              <button
                onClick={() => setRotation((rotation + 90) % 360)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="90° nach rechts"
              >
                <RotateCw className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Position Control - NEW */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-purple-600 mt-0.5">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-purple-900 mb-1">Position anpassen:</p>
                <p className="text-sm text-purple-800">
                  Klicken Sie auf das Bild und ziehen Sie es, um die Position zu ändern. Auf Touch-Geräten: Tippen und schieben.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setCrop({ x: 0, y: 0 })}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg font-semibold transition-colors"
                  >
                    Zentrieren
                  </button>
                  <button
                    onClick={() => {
                      setZoom(0.5);
                      setCrop({ x: 0, y: 0 });
                      setRotation(0);
                    }}
                    className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-lg font-semibold transition-colors"
                  >
                    Alle zurücksetzen
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-lg p-3">
            <p className="text-sm text-[#2B2142]">
              <strong>Anleitung:</strong> 
              1️⃣ Größe anpassen: Slider bewegen oder +/- Buttons nutzen
              2️⃣ Position ändern: Bild im schwarzen Bereich mit Maus ziehen
              3️⃣ Drehen: Drehungs-Slider nutzen oder 90°-Buttons
              4️⃣ Zentrieren: "Zentrieren"-Button klicken
            </p>
          </div>
        </div>
        </div>

        {/* Action Buttons - Fixed Footer */}
        <div className="p-6 border-t-2 border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-100 transition-colors text-lg"
            >
              ❌ Abbrechen
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center justify-center gap-2 text-lg shadow-lg"
            >
              <Check className="w-6 h-6" />
              Position speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureCropModal;
